import {
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MONETIZACAO_GATOR_PROTECT_MIGRATION_MODAL } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT,
  type ConsultedProduct,
  type GatorProtectMigratePortalModalEntry,
} from './MigrateToGatorProtectModal.types';
import { useHistory } from 'react-router';
import { gatorProtectActions } from '@/redux/modules';
import { PORTAL_MODAL_SITELOCK_FREE_ACCEPT } from '@/redux/modules/portalModals/portalModals.constants';
import useLocale from '@/hooks/useLocale';
import type { GPModalsVariants } from '../gatorProtectModalsVisibilityReducer';
import type { HideGatorProtectModalSlot } from '../useGatorProtectModalsVisibility';
import type {
  GatorProtectGlobalsDependencies,
  GatorProtectPortalModalsRootState,
  LocaleWithRoutes,
} from '../GatorProtectGlobals.types';
import type { GatorProtectModalHookReturn } from '../gatorProtectModalHooks.types';
import { FALLBACK_PORTAL_MODALS } from '../gatorProtectPortalModals.shared';

export type UseGatorProtectMigrateModalParams = {
  summary: GatorProtectGlobalsDependencies['summary'];
  isValidPage: boolean;
  hideModalSlot: HideGatorProtectModalSlot;
};

export type UseGatorProtectMigrateModalData = {
  consultedProducts?: ConsultedProduct[];
};

export type UseGatorProtectMigrateModalUtils = {
  hideMigrateModal: (_topStackModalVariant: GPModalsVariants | null) => void;
  redirectMigrateModal: () => void;
};

export type UseGatorProtectMigrateModalReturn = GatorProtectModalHookReturn<
  UseGatorProtectMigrateModalData,
  UseGatorProtectMigrateModalUtils
>;

/**
 * Migration modal from `/api/v3/modals` `sitelock-free-accept` + GrowthBook gate + home + feature flag.
 * PRO product pricing fetch lives in `useGatorProtectGlobal` (`getGatorProtectProProduct`).
 * Closing dispatches `gatorProtectActions.acceptTerms` with the modal hosting id before hiding the dismiss slot.
 */
export function useGatorProtectMigrateModal({
  summary,
  isValidPage,
  hideModalSlot,
}: UseGatorProtectMigrateModalParams): UseGatorProtectMigrateModalReturn {
  const dispatch = useDispatch();
  const history = useHistory();
  const { routes: routesLocale } = useLocale() as LocaleWithRoutes;

  const gatorProtectEnabled = summary.features?.gator_protect ?? false;
  const migrationModalFeatureOn = useFeatureIsOn(MONETIZACAO_GATOR_PROTECT_MIGRATION_MODAL);

  const portalModalsState = useSelector((state: GatorProtectPortalModalsRootState) => state.portalModals);
  const apiModals = portalModalsState ?? FALLBACK_PORTAL_MODALS;

  const consultedProducts = useSelector(
    (state: { products?: { consultedProducts?: ConsultedProduct[] } }) => state.products?.consultedProducts,
  );

  const migratePortalPayload = useMemo((): GatorProtectMigratePortalModalEntry | null => {
    const modalsRaw = apiModals.modals;
    if (!isValidPage || !migrationModalFeatureOn || modalsRaw == null || modalsRaw.length === 0) {
      return null;
    }
    const modals = modalsRaw as GatorProtectMigratePortalModalEntry[];
    const hostingData = modals.find(
      modal => modal?.modal_id === PORTAL_MODAL_SITELOCK_FREE_ACCEPT,
    );
    return hostingData ?? null;
  }, [apiModals.modals, isValidPage, migrationModalFeatureOn]);

  const meetsMigrationRules = Boolean(
    isValidPage
    && gatorProtectEnabled
    && migratePortalPayload !== null,
  );

  const dispatchAcceptTermsForPayload = useCallback((payload: GatorProtectMigratePortalModalEntry | null) => {
    dispatch(gatorProtectActions.acceptTerms.request({
      hostingId: payload?.data?.hosting_id,
      termsAccepted: PORTAL_MODAL_SITELOCK_FREE_ACCEPT,
    }));
  }, [dispatch]);

  const hideMigrateModal = useCallback((topStackModalVariant: GPModalsVariants | null) => {
    if (topStackModalVariant !== GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT) {
      return;
    }
    dispatchAcceptTermsForPayload(migratePortalPayload);
    hideModalSlot(GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT);
  }, [dispatchAcceptTermsForPayload, hideModalSlot, migratePortalPayload]);

  const redirectMigrateModal = useCallback(() => {
    hideModalSlot(GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT);
    const base = routesLocale?.gatorProtect ?? '';
    const hostingId = migratePortalPayload?.data?.hosting_id;
    const suffix = hostingId != null ? `/${hostingId}` : '';
    history.push(`${base}${suffix}`);
  }, [
    hideModalSlot,
    history,
    migratePortalPayload,
    routesLocale?.gatorProtect,
  ]);

  return {
    data: {
      consultedProducts,
    },
    utils: {
      hideMigrateModal,
      redirectMigrateModal,
    },
    visibilityRule: meetsMigrationRules,
  };
}
